import { faEnvelope, faShieldAlt, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { ProfitRoverLogoIcon } from '../generic/ProfitRoverLogos';
import { useRedeemRegistrationCode, useRequestRegistrationCode } from '../../data-access/mutation/stripe';
import captureGoogleAdsConversion from '../../google-analytics/googleAds';
import { gaEmitSubmitRegistrationCodeButtonClick } from '../../google-analytics/signup';
import { ProfitRoverPrimaryButton } from '../forms/ProfitRoverButtons';
import { CenteredProfitRoverSpinner } from '../spinner/ProfitRoverSpinner';
import { REGISTRATION_STEPS, useRegistrationContext } from './registrationContext';
import './redeem-code.scss';

const NotificationToDisplay = props => {
  const { notification } = props;

  if (notification === 'resent') {
    return <div className="resent-email-text">We sent another code to your email!</div>;
  }

  let notificationMessage;
  switch (notification) {
    case 'expired':
      notificationMessage = 'Your registration code has expired! Please resend a new code below.';
      break;
    case 'expired-retry':
      notificationMessage = 'Your registration code has expired! A new one has been emailed to you.';
      break;
    case 'invalid':
      notificationMessage = 'Your registration code is invalid! Please check your code or request a new one below.';
      break;
    case 'resent-request-error':
      notificationMessage =
        'There was an error resending your registration code. Please try again or refresh the page.';
      break;
    case 'request-error':
      notificationMessage =
        'There was an unexpected error while verifying your registration code. Please try again or refresh the page.';
      break;
    default:
      notificationMessage = '';
  }

  return <div className="registration-code-error-text">{notificationMessage}</div>;
};

// TODO: Figure out what to do about this
const PROMO_CODE = null;

const RedeemCode = () => {
  const { setStep, emailAddress, password, selectedPlan } = useRegistrationContext();

  const [registrationCode, setRegistrationCode] = React.useState('');
  const [notificationToDisplay, setNotificationToDisplay] = React.useState('');

  const requestCodeMutation = useRequestRegistrationCode();
  const redeemCodeMutation = useRedeemRegistrationCode();
  const { isLoading: isSubmitting } = redeemCodeMutation;

  const handleResend = async () => {
    try {
      await requestCodeMutation.mutateAsync({ emailAddress, password });
      setNotificationToDisplay('resent');
    } catch (e) {
      setNotificationToDisplay('resent-request-error');
    }
  };

  const autoLogin = () => {
    setStep(REGISTRATION_STEPS.AUTO_LOGIN);
  };

  const onSubmitRegistrationCode = async () => {
    gaEmitSubmitRegistrationCodeButtonClick();

    try {
      const selectedPriceId = selectedPlan.id;

      // Note that we capture Meta ad conversions in the backend upon successful code redemption
      await redeemCodeMutation.mutateAsync({
        registrationCode,
        emailAddress,
        selectedPriceId,
        promoCode: PROMO_CODE,
      });

      captureGoogleAdsConversion(registrationCode, autoLogin);
    } catch (error) {
      const {
        response: { status: statusCode },
      } = error;

      if (statusCode === 403) {
        /**
         * If the server reponds with 403, it should have automatically sent the user an email
         * with a new registration code.
         */
        setNotificationToDisplay('expired-retry');
      } else if (statusCode === 400) {
        setNotificationToDisplay('invalid');
      } else {
        setNotificationToDisplay('request-error');
      }

      setRegistrationCode('');
    }
  };

  const isRegistrationCodeValid = !(registrationCode.length === 6);
  const submitButtonDisabled = isRegistrationCodeValid || isSubmitting;

  return (
    <div className="d-flex flex-column align-items-center">
      <div className="redeem-code-redesign">
        <div className="verification-layout">
          {/* Left side - Progress indicator */}
          <div className="progress-panel">
            <h2 className="progress-headline">Almost there!</h2>
            <p className="progress-subtitle">Just one more step to unlock your profit insights.</p>

            <div className="progress-steps">
              <div className="progress-step completed">
                <div className="step-icon">
                  <FontAwesomeIcon icon={faCheck} />
                </div>
                <span>Account created</span>
              </div>
              <div className="progress-step active">
                <div className="step-icon">
                  <FontAwesomeIcon icon={faEnvelope} />
                </div>
                <span>Verify email</span>
              </div>
              <div className="progress-step">
                <div className="step-icon">3</div>
                <span>Start optimizing</span>
              </div>
            </div>
          </div>

          {/* Right side - Code entry form */}
          <div className="form-panel">
            <div className="redeem-code-container">
              <div className="form-header">
                <ProfitRoverLogoIcon className="form-logo" />
                <p className="title">Check Your Email</p>
              </div>

              <p className="subtitle">
                We sent a 6-digit code to
                <br />
                <strong>{emailAddress}</strong>
              </p>

              <input
                type="text"
                onChange={e => setRegistrationCode(e.target.value)}
                value={registrationCode}
                placeholder="Enter code"
                maxLength={6}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />

              {notificationToDisplay && <NotificationToDisplay notification={notificationToDisplay} />}

              <button type="button" onClick={handleResend} className="resend-code-button">
                Didn&apos;t receive it? Send again
              </button>

              <ProfitRoverPrimaryButton
                className="verify-button"
                disabled={submitButtonDisabled}
                onClick={onSubmitRegistrationCode}
              >
                {isSubmitting ? <CenteredProfitRoverSpinner /> : 'Verify & Continue'}
              </ProfitRoverPrimaryButton>

              <div className="button-micro-caption">
                <FontAwesomeIcon icon={faShieldAlt} className="shield-icon" />
                <span>Your data is encrypted and secure</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedeemCode;
